import { z } from 'zod';
/**
 * Sổ chốt phương án — APM-DSHEET, docs/plan-decision-sheet.md.
 *
 * Một bộ câu hỏi nhiều phương án để người được chỉ định (BA, PO, khách) chốt.
 * Mỗi câu **một đáp án chung**: ai chốt sau thì thay, lịch sử nằm ở event log.
 *
 * Đừng nhầm với RAID `DECISION`: đó là một quyết định đã ghi, còn đây là chỗ
 * quyết định được chọn ra. Một câu đã chốt có thể sinh ra một RAID `DECISION`
 * (APM-DSHEET-f).
 */
// ── Trần — plan §3.3 ────────────────────────────────────────────────────────
export const MAX_DECISION_QUESTIONS = 200;
export const MAX_DECISION_GROUPS = 30;
/** Không kể "Phương án khác" (`X`). */
export const MAX_DECISION_OPTIONS = 8;
export const MIN_DECISION_OPTIONS = 2;
export const MAX_DECISION_ASKS = 10;
export const MAX_DECISION_NOTE = 2_000;
export const MAX_DECISION_DECIDERS = 20;
/** Dưới trần 1 MB của body parser. */
export const MAX_DECISION_IMPORT_BYTES = 500 * 1024;
/** Văn bản / HTML dán vào cho AI trích (APM-DSHEET-h). */
export const MAX_DECISION_PASTE_CHARS = 300_000;
/** Mã dành riêng cho "Phương án khác" — chọn nó thì bắt buộc có ghi chú. */
export const DECISION_OTHER_KEY = 'X';
export const DECISION_FILE_FORMAT = 'apm-decision-sheet';
export const DECISION_FILE_VERSION = 1;
// ── Mảnh dùng chung ─────────────────────────────────────────────────────────
export const DecisionSheetStatus = z.enum(['draft', 'open', 'closed']);
export const DecisionSheetSource = z.enum(['manual', 'import', 'ai']);
/**
 * Câu này có trong tài liệu nguồn nhưng chưa đủ rõ (`documented`) hay người
 * soạn nêu thêm (`new`). Trang mẫu gọi là "Tài liệu đã có" / "Chưa nói đến".
 */
export const DecisionQuestionOrigin = z.enum(['documented', 'new']);
/** Mã phương án: một chữ in hoa, trừ `X`. */
export const DecisionOptionKey = z
    .string()
    .trim()
    .regex(/^[A-W]$/, 'Mã phương án là một chữ in hoa A–W (X dành cho "Phương án khác")');
/** Mã đáp án: mã một phương án, hoặc `X`. */
export const DecisionAnswerKey = z
    .string()
    .trim()
    .regex(/^[A-X]$/);
/**
 * Đáp án của một câu ở **mọi tầng** (đầu vào API, view, cột `answer_key`, event):
 * một mã (`"D"`) với câu chọn một, hoặc các mã nối bằng dấu phẩy (`"A,C,X"`)
 * với câu chọn nhiều — APM-DSX-multi, 2026-10-07. Một chuỗi chứ không phải mảng
 * để mọi chỗ đang hỏi "đã chốt chưa / chốt gì" (đếm, so, lịch sử, Replay) chạy
 * nguyên cho event cũ lẫn mới. Dạng chuẩn do `canonicalDecisionKeys` dựng: bỏ
 * trùng, xếp A→X (X luôn cuối).
 */
export const DecisionAnswerKeys = z
    .string()
    .trim()
    .max(40)
    .regex(/^[A-X](\s*,\s*[A-X])*$/, 'Đáp án là mã phương án A–X, nhiều mã thì cách nhau bằng dấu phẩy');
/** `"A, C,X"` ⇒ `["A","C","X"]`. */
export function splitDecisionKeys(key) {
    if (!key)
        return [];
    return key
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '');
}
/** Dạng chuẩn để lưu và so: bỏ trùng, xếp A→X. `[]` ⇒ `''`. */
export function canonicalDecisionKeys(keys) {
    return [...new Set(keys)].sort().join(',');
}
const Code = z.string().trim().min(1).max(20);
const Title = z.string().trim().min(1).max(500);
const LongText = z.string().trim().max(5_000);
const Note = z.string().trim().max(MAX_DECISION_NOTE);
/**
 * "Không có" theo cách AI Agent ngoài hay viết — `null`, `""`, chỉ dấu cách —
 * coi như **bỏ trường**. Tệp nhập thường do Claude/ChatGPT/Gemini soạn, và
 * chúng điền `null` cho trường tuỳ chọn thay vì bỏ đi; báo lỗi cho chuyện đó
 * chỉ khiến người dùng sửa tay hàng chục chỗ vô nghĩa (2026-10-07: một tệp 6
 * nhóm đỏ cả 6 `highlight`).
 */
function absent(value) {
    if (value === null)
        return undefined;
    if (typeof value === 'string' && value.trim() === '')
        return undefined;
    return value;
}
const optionalField = (schema) => z.preprocess(absent, schema.optional());
export const DecisionOption = z.object({
    key: DecisionOptionKey,
    title: z.string().trim().min(1).max(300),
    detail: optionalField(z.string().trim().max(2_000)),
    /** Đề xuất của người soạn — tối đa một phương án mỗi câu; câu chọn nhiều thì không có. */
    recommended: optionalField(z.boolean()),
});
const Options = z.array(DecisionOption).min(MIN_DECISION_OPTIONS).max(MAX_DECISION_OPTIONS);
const Asks = z.array(z.string().trim().min(1).max(500)).max(MAX_DECISION_ASKS);
/**
 * Luật chung của danh sách phương án: mã không trùng, tối đa một "nên chọn",
 * và câu chọn nhiều thì **không** có "nên chọn" (người dùng chốt 2026-10-07).
 * Dùng cho cả tệp JSON lẫn lệnh sửa câu.
 */
function checkOptions(options, ctx, path, multiple = false) {
    const seen = new Set();
    options.forEach((option, i) => {
        if (seen.has(option.key)) {
            ctx.addIssue({
                code: 'custom',
                path: [...path, i, 'key'],
                message: `Trùng mã phương án ${option.key}`,
            });
        }
        seen.add(option.key);
    });
    const recommended = options.filter((o) => o.recommended).length;
    if (multiple && recommended > 0) {
        ctx.addIssue({
            code: 'custom',
            path,
            message: 'Câu chọn nhiều đáp án không có phương án "nên chọn" — bỏ "recommended"',
        });
    }
    else if (recommended > 1) {
        ctx.addIssue({ code: 'custom', path, message: 'Tối đa một phương án "nên chọn"' });
    }
}
// ── Tệp JSON `apm-decision-sheet` v1 — plan §5.2 ────────────────────────────
export const DecisionFileGroup = z.object({
    code: Code,
    title: z.string().trim().min(1).max(300),
    note: optionalField(LongText),
    /**
     * Nhãn nổi bật của nhóm, vd. "Ảnh hưởng KPI / tiền" — **chữ**, không phải cờ.
     * `false` = không có nhãn; `true` báo lỗi có hướng dẫn: trang mẫu gốc đánh dấu
     * nhóm bằng cờ `tien: true`, nên AI hay chép thành `"highlight": true`.
     */
    highlight: z.preprocess((value) => (value === false ? undefined : absent(value)), z
        .string({
        error: (issue) => issue.input === true
            ? 'highlight là nhãn chữ ngắn (vd. "Ảnh hưởng KPI / tiền"), không phải true — nhóm không cần nhãn thì bỏ trường này'
            : 'highlight phải là một nhãn chữ ngắn, vd. "Ảnh hưởng KPI / tiền"',
    })
        .trim()
        .max(60, 'Nhãn nổi bật tối đa 60 ký tự')
        .optional()),
});
export const DecisionFileQuestion = z.object({
    code: Code,
    /** `groups[].code`; bỏ trống = câu không nhóm. */
    group: optionalField(Code),
    title: Title,
    origin: optionalField(DecisionQuestionOrigin),
    /** Trích chỗ trong tài liệu nguồn ("File 2, sheet 3, ô A34–A35") hoặc "Không có." */
    sourceRef: optionalField(LongText),
    /** Tình huống: vì sao câu này quan trọng. */
    context: optionalField(LongText),
    /** Những điểm cần chốt. */
    asks: z.preprocess((value) => value ?? [], Asks),
    /** Cho chọn nhiều đáp án — APM-DSX-multi. Bỏ trống / `false` = chọn một. */
    multiple: optionalField(z.boolean()),
    options: Options,
});
/**
 * `key` là `DecisionAnswerKeys` (`"D"` hoặc `"A,C"`). AI Agent ngoài hay viết
 * mảng — `"keys": ["A","C"]` — nên mảng ấy được gộp thành `key` trước khi kiểm.
 */
export const DecisionFileAnswer = z.preprocess((value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const raw = value;
        if (Array.isArray(raw.keys) && (raw.key === undefined || raw.key === null)) {
            const { keys, ...rest } = raw;
            return { ...rest, key: keys.map(String).join(',') };
        }
    }
    return value;
}, z.object({
    code: Code,
    key: DecisionAnswerKeys,
    note: optionalField(Note),
}));
/**
 * Định dạng nhập/xuất. Xuất rồi nhập lại phải ra đúng sổ cũ (test khứ hồi).
 *
 * `superRefine` báo **mọi** lỗi kèm đường dẫn chứ không dừng ở lỗi đầu: người
 * dán một tệp 32 câu cần sửa một lần, không phải 32 lần.
 */
export const DecisionSheetFile = z
    .object({
    format: z.literal(DECISION_FILE_FORMAT),
    version: z.literal(DECISION_FILE_VERSION),
    title: z.string().trim().min(1).max(300),
    description: optionalField(LongText),
    allowOther: z.preprocess((value) => value ?? true, z.boolean()),
    groups: z.preprocess((value) => value ?? [], z.array(DecisionFileGroup).max(MAX_DECISION_GROUPS)),
    questions: z.array(DecisionFileQuestion).min(1).max(MAX_DECISION_QUESTIONS),
    /** Tuỳ chọn — chuyển một sổ đã chốt dở từ nơi khác (plan §5.2). */
    answers: optionalField(z.array(DecisionFileAnswer).max(MAX_DECISION_QUESTIONS)),
})
    .superRefine((file, ctx) => {
    const groupCodes = new Set();
    file.groups.forEach((g, i) => {
        if (groupCodes.has(g.code)) {
            ctx.addIssue({
                code: 'custom',
                path: ['groups', i, 'code'],
                message: `Trùng mã nhóm ${g.code}`,
            });
        }
        groupCodes.add(g.code);
    });
    const questions = new Map();
    file.questions.forEach((q, i) => {
        if (questions.has(q.code)) {
            ctx.addIssue({
                code: 'custom',
                path: ['questions', i, 'code'],
                message: `Trùng mã câu ${q.code}`,
            });
        }
        questions.set(q.code, q);
        if (q.group !== undefined && !groupCodes.has(q.group)) {
            ctx.addIssue({
                code: 'custom',
                path: ['questions', i, 'group'],
                message: `Không có nhóm ${q.group}`,
            });
        }
        checkOptions(q.options, ctx, ['questions', i, 'options'], q.multiple === true);
    });
    const answered = new Set();
    file.answers?.forEach((a, i) => {
        const q = questions.get(a.code);
        if (!q) {
            ctx.addIssue({
                code: 'custom',
                path: ['answers', i, 'code'],
                message: `Không có câu ${a.code}`,
            });
            return;
        }
        if (answered.has(a.code)) {
            ctx.addIssue({
                code: 'custom',
                path: ['answers', i, 'code'],
                message: `Câu ${a.code} có hai đáp án`,
            });
        }
        answered.add(a.code);
        checkAnswer(a.key, a.note, q, file.allowOther, ctx, ['answers', i]);
    });
});
function checkAnswer(key, note, question, allowOther, ctx, path) {
    const keys = splitDecisionKeys(key);
    if (question.multiple !== true && keys.length > 1) {
        ctx.addIssue({
            code: 'custom',
            path: [...path, 'key'],
            message: `Câu ${question.code} chỉ chọn một phương án — muốn chọn nhiều thì đặt "multiple": true cho câu`,
        });
    }
    for (const one of new Set(keys)) {
        if (one === DECISION_OTHER_KEY) {
            if (!allowOther) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...path, 'key'],
                    message: 'Sổ này không cho "Phương án khác"',
                });
            }
            if (!note?.trim()) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...path, 'note'],
                    message: '"Phương án khác" cần ghi chú',
                });
            }
        }
        else if (!question.options.some((o) => o.key === one)) {
            ctx.addIssue({ code: 'custom', path: [...path, 'key'], message: `Không có phương án ${one}` });
        }
    }
}
/** Đáp án có `X` mà thiếu ghi chú — luật của cả đầu vào nội bộ lẫn link công khai. */
function otherNeedsNote(v, ctx) {
    if (splitDecisionKeys(v.key).includes(DECISION_OTHER_KEY) && !v.note.trim()) {
        ctx.addIssue({ code: 'custom', path: ['note'], message: '"Phương án khác" cần ghi chú' });
    }
}
export function decisionImportIssues(error) {
    return error.issues.map((issue) => ({
        path: issue.path
            .map((p, i) => (typeof p === 'number' ? `[${p}]` : i === 0 ? String(p) : `.${String(p)}`))
            .join(''),
        message: importIssueMessage(issue),
    }));
}
const TYPE_VI = {
    string: 'chuỗi chữ',
    number: 'số',
    boolean: 'true/false',
    array: 'mảng [...]',
    object: 'đối tượng {...}',
    null: 'null',
    undefined: 'không có',
};
/**
 * Câu lỗi tiếng Việt cho người sửa tệp. Câu mặc định của Zod là tiếng Anh, và
 * ở một số bản build chỉ còn "Invalid input" — không nói được sai ở đâu. Lỗi tự
 * viết (`custom`, hay thông điệp riêng như của `highlight`) giữ nguyên.
 */
function importIssueMessage(issue) {
    const own = !/^(Invalid|Too (small|big)|Unrecognized)/.test(issue.message);
    if (issue.code === 'custom' || own)
        return issue.message;
    switch (issue.code) {
        case 'invalid_type': {
            const received = /received (\w+)/.exec(issue.message)?.[1];
            if (received === 'undefined')
                return 'Thiếu trường bắt buộc';
            const want = TYPE_VI[issue.expected] ?? issue.expected;
            return received
                ? `Sai kiểu: cần ${want}, đang là ${TYPE_VI[received] ?? received}`
                : `Sai kiểu: cần ${want}`;
        }
        case 'too_small':
            if (issue.origin === 'string') {
                return Number(issue.minimum) <= 1
                    ? 'Không được để trống'
                    : `Cần ít nhất ${issue.minimum} ký tự`;
            }
            return issue.origin === 'array'
                ? `Cần ít nhất ${issue.minimum} mục`
                : `Nhỏ nhất là ${issue.minimum}`;
        case 'too_big':
            if (issue.origin === 'string')
                return `Tối đa ${issue.maximum} ký tự`;
            return issue.origin === 'array'
                ? `Tối đa ${issue.maximum} mục`
                : `Lớn nhất là ${issue.maximum}`;
        case 'invalid_value':
            return `Phải là ${issue.values.map((v) => JSON.stringify(v)).join(' hoặc ')}`;
        case 'invalid_format':
            return 'Sai định dạng';
        default:
            return issue.message;
    }
}
// ── Input của lệnh ──────────────────────────────────────────────────────────
/** Tạo sổ: rỗng (chỉ tiêu đề) hoặc từ một tệp JSON v1. Luôn ra `draft`. */
export const CreateDecisionSheetInput = z.union([
    z.object({
        title: z.string().trim().min(1).max(300),
        description: LongText.optional(),
        allowOther: z.boolean().optional(),
    }),
    z.object({ file: DecisionSheetFile }),
]);
export const UpdateDecisionSheetInput = z
    .object({
    title: z.string().trim().min(1).max(300).optional(),
    description: LongText.nullable().optional(),
    allowOther: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Không có gì để sửa');
export const UpsertDecisionGroupInput = z.object({
    /** Có ⇒ sửa nhóm này; không ⇒ thêm nhóm mới (cuối sổ). */
    groupId: z.uuid().optional(),
    code: Code,
    title: z.string().trim().min(1).max(300),
    note: LongText.nullable().optional(),
    highlight: z.string().trim().min(1).max(60).nullable().optional(),
});
export const UpsertDecisionQuestionInput = z
    .object({
    /** Có ⇒ sửa câu này; không ⇒ thêm câu mới (cuối nhóm). */
    questionId: z.uuid().optional(),
    groupId: z.uuid().nullable().optional(),
    code: Code,
    title: Title,
    origin: DecisionQuestionOrigin.nullable().optional(),
    sourceRef: LongText.nullable().optional(),
    context: LongText.nullable().optional(),
    asks: Asks.default([]),
    /** Cho chọn nhiều đáp án — APM-DSX-multi. Bỏ trống khi sửa = giữ như cũ; câu mới = chọn một. */
    multiple: z.boolean().optional(),
    options: Options,
})
    .superRefine((q, ctx) => checkOptions(q.options, ctx, ['options'], q.multiple ?? false));
export const ReorderDecisionQuestionsInput = z.object({
    items: z
        .array(z.object({
        questionId: z.uuid(),
        groupId: z.uuid().nullable(),
        position: z.number().int().min(0),
    }))
        .min(1)
        .max(MAX_DECISION_QUESTIONS),
});
export const ChangeDecisionDecidersInput = z
    .object({
    add: z.array(z.uuid()).max(MAX_DECISION_DECIDERS).default([]),
    remove: z.array(z.uuid()).max(MAX_DECISION_DECIDERS).default([]),
})
    .refine((v) => v.add.length + v.remove.length > 0, 'Không có gì để đổi');
/**
 * Chốt (hoặc sửa ghi chú — cùng `key`). `baseRev` là `answerRev` người dùng
 * đang nhìn thấy; lệch ⇒ 409 `DECISION_ANSWER_CONFLICT` (plan §4.3).
 */
export const AnswerDecisionInput = z
    .object({
    /** `"D"`, hoặc `"A,C,X"` cho câu chọn nhiều — xem `DecisionAnswerKeys`. */
    key: DecisionAnswerKeys,
    note: Note.default(''),
    baseRev: z.number().int().min(0),
})
    .superRefine(otherNeedsNote);
export const ClearDecisionAnswerInput = z.object({ baseRev: z.number().int().min(0) });
/**
 * Định dạng của "Xuất thành tài liệu dự án" (APM-DSX, plan §6.1). Mỗi định dạng
 * một tài liệu dự án riêng — lịch sử phiên bản của một tài liệu không trộn
 * `.md` với `.chilldoc`.
 */
export const DecisionExportFormat = z.enum(['chilldoc', 'md']);
/**
 * APM-DSHEET-e — sau khi web đã tạo tài liệu / thêm phiên bản. Thiếu `format`
 * = `chilldoc` (web trước APM-DSX). Server chỉ nhận khi sổ đã đóng
 * (`DECISION_SHEET_NOT_CLOSED`).
 */
export const RecordDecisionExportInput = z.object({
    documentId: z.uuid(),
    version: z.number().int().min(1),
    format: DecisionExportFormat.default('chilldoc'),
});
/** APM-DSHEET-f — sau khi web đã tạo các RAID `DECISION`. */
export const RecordDecisionRaidInput = z.object({
    items: z
        .array(z.object({ questionId: z.uuid(), raidItemId: z.uuid(), rev: z.number().int().min(1) }))
        .min(1)
        .max(MAX_DECISION_QUESTIONS),
});
// ── Hàm thuần dùng chung ────────────────────────────────────────────────────
/** Đáp án có trùng phương án "nên chọn" không — cho ô "Chọn đúng đề xuất". */
export function isRecommendedAnswer(question, key) {
    if (!key)
        return false;
    return question.options.some((o) => o.key === key && o.recommended === true);
}
/** Gợi ý mã câu kế tiếp theo mẫu của câu cuối (`A-09` ⇒ `A-10`). */
export function nextDecisionCode(codes) {
    const last = codes.at(-1);
    const match = last?.match(/^(.*?)(\d+)$/);
    if (!match)
        return `Q-${String(codes.length + 1).padStart(2, '0')}`;
    const [, prefix, digits] = match;
    const next = String(Number(digits) + 1).padStart(digits.length, '0');
    return `${prefix}${next}`;
}
// ── AI — APM-DSHEET-g/h, plan §5.4–5.5 ──────────────────────────────────────
/** Skill soạn sổ từ tài liệu dự án — APM-DSHEET-g. Lưu ở `ai_suggestion.skill`. */
export const DECISION_SHEET_DRAFT_SKILL = 'decision_sheet.draft';
/** Skill trích sổ từ văn bản / HTML dán vào — APM-DSHEET-h. */
export const DECISION_SHEET_EXTRACT_SKILL = 'decision_sheet.extract';
/** Số tài liệu tối đa một lượt soạn đọc được (plan §5.4). */
export const MAX_DECISION_DRAFT_DOCUMENTS = 5;
export const MAX_DECISION_DRAFT_GOAL = 500;
/** `POST /projects/:projectId/decision-sheets/ai/draft` */
export const DraftDecisionSheetInput = z.object({
    documentIds: z
        .array(z.uuid())
        .min(1)
        .max(MAX_DECISION_DRAFT_DOCUMENTS)
        .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, {
        message: 'Một tài liệu được chọn hai lần',
    }),
    /** Mục tiêu ngắn: "những chỗ BA chưa chốt ảnh hưởng tới tính tiền". */
    goal: z.string().trim().max(MAX_DECISION_DRAFT_GOAL).optional(),
    /** Mã dấu vết do client sinh, mở SSE `/ai/runs/:runId/stream` trước khi POST (APM-1302). */
    runId: z.uuid().optional(),
});
/**
 * `POST /projects/:projectId/decision-sheets/ai/extract`
 *
 * Client bỏ thẻ `<style>` trước khi gửi (plan §5.5); server bỏ thêm lần nữa
 * trước khi đưa model, nên client quên cũng chỉ tốn băng thông.
 */
export const ExtractDecisionSheetInput = z.object({
    text: z.string().trim().min(1).max(MAX_DECISION_PASTE_CHARS),
    runId: z.uuid().optional(),
});
/**
 * `POST /decision-sheets/ai/suggestions/:suggestionId/accept` — "Tạo sổ" từ một
 * gợi ý của `decision_sheet.draft` / `decision_sheet.extract` (APM-DSHEET-g/h).
 *
 * `file` là bản người duyệt đã sửa trước khi bấm; không có ⇒ dùng nguyên
 * `content` của gợi ý. Bản soạn từ tài liệu (`draft`) không bao giờ mang
 * `answers` — server bỏ đi dù web có gửi. Server validate `file` **riêng** để
 * trả mọi lỗi theo đường dẫn (`DECISION_IMPORT_INVALID`), như đường nhập JSON.
 *
 * Trả `{ id }` của sổ mới (`draft`, `source = 'ai'`), 201.
 */
export const AcceptDecisionSuggestionInput = z.object({
    file: DecisionSheetFile.optional(),
});
/** `POST /decision-sheets/ai/suggestions/:suggestionId/reject` ⇒ `{ ok: true }`. */
export const RejectDecisionSuggestionInput = z.object({
    /** Lý do bỏ — dữ liệu để cải thiện gợi ý về sau (spec 12.1). */
    reason: z.string().trim().max(500).optional(),
});
// ── Đợt 2 (APM-DSX) — plan §11 ──────────────────────────────────────────────
/** Trần số link công khai còn hiệu lực trên một sổ. */
export const MAX_DECISION_SHARE_LINKS = 10;
/** Hạn tối đa đặt tay cho link (ngày) — cùng trần với chia sẻ tệp. */
export const MAX_DECISION_SHARE_DAYS = 366;
/**
 * `view` — người ngoài chỉ xem đáp án; `answer` — được chốt như một người chốt.
 * Link `answer` chỉ chốt được khi sổ `open`.
 */
export const DecisionShareMode = z.enum(['view', 'answer']);
export const CreateDecisionShareLinkInput = z.object({
    mode: DecisionShareMode.default('answer'),
    /** `null` = không hạn. */
    expiresInDays: z.number().int().min(1).max(MAX_DECISION_SHARE_DAYS).nullable().default(30),
});
/**
 * Người trả lời qua link khi **chưa đăng nhập** — không xác minh email (người
 * dùng chốt 2026-10-07). Đã đăng nhập thì server lấy tên/email từ tài khoản.
 */
export const DecisionRespondent = z.object({
    name: z.string().trim().min(1).max(120),
    email: z.email().trim().max(254),
});
/** Chốt qua link công khai. `respondent` bắt buộc khi chưa đăng nhập. */
export const PublicAnswerDecisionInput = z
    .object({
    key: DecisionAnswerKeys,
    note: Note.default(''),
    baseRev: z.number().int().min(0),
    respondent: DecisionRespondent.optional(),
})
    .superRefine(otherNeedsNote);
export const PublicClearDecisionAnswerInput = z.object({
    baseRev: z.number().int().min(0),
    respondent: DecisionRespondent.optional(),
});
// Giao việc trả lời — task nhúng sổ (APM-DSX-c/d)
/** Gắn một task có sẵn vào sổ; người được giao task thành người chốt của sổ. */
export const LinkDecisionTaskInput = z.object({ taskId: z.uuid() });
//# sourceMappingURL=decision-sheet.js.map